import { Chart } from "@/components/charts/Chart"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

/**
 * EL DETALLE DE UNA CIFRA, en una ventana y no en otra pantalla.
 *
 * Cesar, 2026-10-04: «metiendo más pop ups». Una tarjeta del dashboard dice
 * «Ventas: 412.000» y la pregunta siguiente es siempre la misma: ¿y cómo
 * fue mes a mes? Hasta hoy la respuesta era ir a Finanzas, buscar la fila y
 * volver. Ahora la tarjeta se abre y la respuesta está encima de la pantalla
 * de la que se vino, que es donde se quería estar.
 *
 * Es SOLO LECTURA a propósito: lo que se edita se edita en su pantalla, con su
 * motivo y su auditoría. Esto es una lupa, no un formulario.
 */
export interface DetalleDeCifra {
  etiquetas: string[]
  valores: Array<number | null>
  /** Segunda serie, opcional: el mismo período del año anterior, por ejemplo. */
  comparar?: { nombre: string; valores: Array<number | null> }
  /** Índice del último punto REAL: dibuja la línea roja del corte. */
  corte?: number | null
  /** Qué se está mirando. Si no se da, el rótulo de la tarjeta. */
  nota?: string
}

export function MetricDetailDialog({
  open, onOpenChange, titulo, detalle, integer,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  titulo: string
  detalle: DetalleDeCifra
  integer?: boolean
}) {
  const formato = integer ? (v: number) => String(Math.round(v)) : formatAmount
  const series = [{ nombre: titulo, valores: detalle.valores }]
  if (detalle.comparar) series.push(detalle.comparar)
  const total = detalle.valores.reduce<number>((s, v) => s + (v ?? 0), 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="titular punto">{titulo}</DialogTitle>
          <DialogDescription>
            {detalle.nota ?? t("Cómo fue, período a período. Para cambiar algo se entra a la pantalla que lo arma.")}
          </DialogDescription>
        </DialogHeader>

        <Chart etiquetas={detalle.etiquetas} series={series} alto={180}
               formato={formato} corte={detalle.corte ?? null} />

        <table className="w-full text-2xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="eyebrow py-1 font-normal">{t("Período")}</th>
              <th className="eyebrow py-1 text-right font-normal">{titulo}</th>
              {detalle.comparar ? (
                <th className="eyebrow py-1 text-right font-normal">{detalle.comparar.nombre}</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {detalle.etiquetas.map((e, i) => {
              const v = detalle.valores[i]
              const esUltimoReal = detalle.corte != null && i === detalle.corte
              return (
                <tr key={e} className={cn("border-t", esUltimoReal && "corte-real")}>
                  <td className="py-1 text-muted-foreground">{e}</td>
                  <td className="num py-1 text-right">{v == null ? "—" : formato(v)}</td>
                  {detalle.comparar ? (
                    <td className="num py-1 text-right text-muted-foreground">
                      {detalle.comparar.valores[i] == null ? "—" : formato(detalle.comparar.valores[i] as number)}
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
          {!integer ? (
            <tfoot>
              <tr className="border-t">
                <td className="py-1 font-medium">{t("Total")}</td>
                <td className="num py-1 text-right font-medium">{formato(total)}</td>
                {detalle.comparar ? <td /> : null}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </DialogContent>
    </Dialog>
  )
}
